import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { ModuleTask } from 'module/page/task/module-task';
import { ModuleTaskI18n } from 'module/page/task/module-task-i18n';
import { TaskType } from 'task/model/task.type';

import { AppLocator } from '../../../../app.locator';

export interface ModuleTaskI18nDocument {
	readonly title: string;
	readonly locale: string;
	readonly category: string;
	readonly description: string;
}

export interface ModuleTaskDocument {
	readonly id: string;
	readonly type: TaskType;
	readonly position: number;
	readonly updateTimestamp: number;
	readonly localizations: ModuleTaskI18nDocument[];
}

export type ModuleTaskState = EntityState<ModuleTaskDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'moduleTasks' })
export class ModuleTaskStore extends EntityStore<ModuleTaskState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(moduleTask: ModuleTask): ModuleTaskDocument {
		return moduleTask
			? {
					localizations: moduleTask.localizations.map((loc) => ({
						description: loc.description,
						locale: loc.locale,
						title: loc.title,
						category: loc.category,
					})),
					updateTimestamp: moduleTask.updateTimestamp,
					position: moduleTask.position,
					type: moduleTask.type,
					id: moduleTask.id,
			  }
			: null;
	}

	public asEntity(document: ModuleTaskDocument): ModuleTask {
		const localizations = !!document
			? (document.localizations || []).map(
					(loc) =>
						new ModuleTaskI18n(loc.title, loc.locale, loc.category, loc.description)
			  )
			: [];

		return document
			? new ModuleTask(
					document.id,
					document.type,
					document.position,
					document.updateTimestamp,
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
